/* Desktop mega nav.

   The brief specifies hover-to-open, which on its own excludes keyboard and
   touch entirely. This adds three more ways in without changing the pointer
   behaviour a mouse user sees:

     pointerenter  opens — guarded on pointerType so a touch does not fire
                   hover AND click and immediately toggle the panel shut
     focusin       opens, so tabbing to a trigger reveals its panel
     click         toggles, which is what a touch device gets, on a <button>
                   trigger. A trigger with a link (an <a href>, 3 Nov 2026)
                   navigates on a mouse click or Enter; on a TOUCH the first
                   tap opens its panel (preventDefault) and a second follows
                   the link, so a tablet at desktop width still reaches it

   Escape closes and returns focus to the trigger that opened the panel —
   without that, focus is left on an element inside a panel that is now
   display:none and the browser drops it to <body>.

   Close is intentionally delayed. The pointer has to cross a gap between the
   trigger row and the panel; closing on the first mouseleave makes the panel
   flicker shut mid-travel. Any re-entry inside CLOSE_DELAY cancels it.

   Desktop only. The panel is display:none under 990px and the hamburger
   drawer owns navigation there, so the listeners are never attached on
   mobile and there is nothing to tear down at a breakpoint change.
*/
(() => {
  const DESKTOP = '(min-width: 990px)';
  const CLOSE_DELAY = 180;

  class RbMegaNav {
    constructor(root) {
      this.root = root;
      this.triggers = [...root.querySelectorAll('[data-mega-trigger]')];
      this.panels = [...root.querySelectorAll('[data-mega-panel]')];
      this.openKey = null;
      this.closeTimer = null;
      this.lastTrigger = null;
      this.bind();
    }

    panelFor(key) {
      return this.panels.find((p) => p.getAttribute('data-mega-panel') === key) || null;
    }

    open(key, trigger) {
      clearTimeout(this.closeTimer);
      if (this.openKey === key) return;
      this.closeNow();
      const panel = this.panelFor(key);
      if (!panel) return;
      panel.hidden = false;
      panel.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      trigger.classList.add('is-active');
      this.openKey = key;
      this.lastTrigger = trigger;
      this.root.classList.add('rb-mega--open');
    }

    closeNow() {
      this.panels.forEach((p) => {
        p.hidden = true;
        p.classList.remove('is-open');
      });
      this.triggers.forEach((t) => {
        t.setAttribute('aria-expanded', 'false');
        t.classList.remove('is-active');
      });
      this.openKey = null;
      this.root.classList.remove('rb-mega--open');
    }

    /* Delayed so crossing the gap between trigger row and panel does not
       flicker. Re-entering anywhere in the nav cancels it. */
    closeSoon() {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.closeNow(), CLOSE_DELAY);
    }

    bind() {
      this.triggers.forEach((trigger) => {
        const key = trigger.getAttribute('data-mega-trigger');

        trigger.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'touch') return;
          this.open(key, trigger);
        });

        trigger.addEventListener('focusin', () => this.open(key, trigger));

        /* The press that produces the next click: its pointer type, and
           whether this panel was open BEFORE it. Read at press time because a
           tap focuses the link first, and focusin opens the panel before the
           click arrives — checking at click time always saw it open. */
        let pressType = null;
        let openAtPress = false;
        trigger.addEventListener('pointerdown', (event) => {
          pressType = event.pointerType;
          openAtPress = this.openKey === key;
        });

        trigger.addEventListener('click', (event) => {
          if (trigger.tagName === 'A') {
            const touch = pressType === 'touch';
            const wasOpen = openAtPress;
            pressType = null;
            openAtPress = false;
            if (touch && !wasOpen) {
              event.preventDefault();
              this.open(key, trigger);
            }
            return;
          }
          if (this.openKey === key) {
            this.closeNow();
          } else {
            this.open(key, trigger);
          }
        });
      });

      /* One pair of listeners on the wrapper covers the trigger row and every
         panel, so travelling between them never leaves the nav. */
      this.root.addEventListener('pointerenter', () => clearTimeout(this.closeTimer), true);
      this.root.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        this.closeSoon();
      });

      this.root.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || this.openKey === null) return;
        const trigger = this.lastTrigger;
        this.closeNow();
        if (trigger) trigger.focus();
      });

      /* Focus leaving the nav entirely closes it. relatedTarget is null when
         focus goes to the browser chrome, which should not count as leaving. */
      this.root.addEventListener('focusout', (event) => {
        if (!event.relatedTarget) return;
        if (this.root.contains(event.relatedTarget)) return;
        this.closeNow();
      });

      document.addEventListener('click', (event) => {
        if (this.openKey === null) return;
        if (this.root.contains(event.target)) return;
        this.closeNow();
      });
    }
  }

  const mount = () => {
    if (!window.matchMedia(DESKTOP).matches) return;
    document.querySelectorAll('[data-mega-nav]').forEach((root) => {
      if (root.dataset.megaMounted) return;
      root.dataset.megaMounted = 'true';
      new RbMegaNav(root);
    });
  };

  document.addEventListener('DOMContentLoaded', mount);
  window.matchMedia(DESKTOP).addEventListener('change', mount);
  /* Re-mount when the section is re-rendered in the theme editor. */
  document.addEventListener('shopify:section:load', mount);
})();
